import { useForm, Controller } from 'react-hook-form';
import { useAppSelector, useAppDispatch } from 'store/index';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import { SettingsMain, SettingsPageHeader } from '../SettingsLayout';
import CommerceTypeAvatar from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import { CommerceType } from 'types/models/company';
import Form from 'storybookConfig/stories/organisms/Form';
import Input from 'storybookConfig/stories/molecules/Input';
import Select from 'storybookConfig/stories/molecules/Select';
import Checkbox from 'storybookConfig/stories/molecules/Checkbox';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import type { Company, ShippingRate } from 'types/models/company';
import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import Heading from 'storybookConfig/stories/molecules/Heading';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import { COUNTRIES } from 'utils/countries';
import type { ReactSelectOption } from 'types/general';

const ShippingSection = styled.div`
  width: 100%;
`;

const ShippingGroup = styled(Flex)`
  margin-bottom: 24px;
  width: 100%;
`;

const RateContainer = styled(Flex)`
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const SectionTitle = styled(Heading)`
  margin-bottom: 24px;
`;

// Reusable validation function for shipping method inputs
const validateShippingMethod = (rateValue: string | number | undefined, errorMessage: string) => {
  return (value: string | undefined) => {
    if (rateValue && rateValue !== '' && rateValue !== '0' && (!value || value.trim() === '')) {
      return errorMessage;
    }
    return true;
  };
};

interface FormFields {
  shippingCountry?: string;
  shippingRates?: {
    us?: Partial<ShippingRate> | null;
    international?: Partial<ShippingRate> | null;
  };
  isInternationalShippingDisabled?: boolean;
}

const Shipping = () => {
  const company = useAppSelector(selectCompany);
  const companyId = useAppSelector(selectCompanyId);
  const { commerceType } = company ?? {};
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const dispatch = useAppDispatch();

  const { handleSubmit, register, watch, control, formState } = useForm<FormFields>({
    mode: 'onChange',
    values: {
      shippingCountry: company?.marketplaceSettings?.shippingCountry || '',
      shippingRates: company?.marketplaceSettings?.shippingRates || {
        us: null,
        international: null,
      },
      isInternationalShippingDisabled: !company?.marketplaceSettings?.shippingRates?.international,
    },
  });

  const isInternationalDisabled = watch('isInternationalShippingDisabled');

  // Watch shipping rate values for real-time validation
  const usFlatRate = watch('shippingRates.us.flatRate');
  const usFreeShippingThreshold = watch('shippingRates.us.freeShippingThreshold');
  const internationalFlatRate = watch('shippingRates.international.flatRate');
  const internationalFreeShippingThreshold = watch(
    'shippingRates.international.freeShippingThreshold'
  );

  const { mutate: updateShipping, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: (data: FormFields) => {
      const internationalRates = data.isInternationalShippingDisabled
        ? null
        : data.shippingRates?.international;

      const updatedData = {
        shippingCountry: data.shippingCountry,
        shippingRates: {
          us: {
            flatRate: data.shippingRates?.us?.flatRate
              ? Number(data.shippingRates.us.flatRate)
              : (null as any),
            flatRateMethod: data.shippingRates?.us?.flatRateMethod || '',
            freeShippingThreshold: data.shippingRates?.us?.freeShippingThreshold
              ? Number(data.shippingRates.us.freeShippingThreshold)
              : (null as any),
            freeShippingMethod: data.shippingRates?.us?.freeShippingMethod || '',
          },
          international:
            internationalRates && internationalRates.flatRate
              ? {
                  flatRate: internationalRates.flatRate
                    ? Number(internationalRates.flatRate)
                    : (null as any),
                  flatRateMethod: internationalRates.flatRateMethod || '',
                  freeShippingThreshold: internationalRates.freeShippingThreshold
                    ? Number(internationalRates.freeShippingThreshold)
                    : (null as any),
                  freeShippingMethod: internationalRates.freeShippingMethod || '',
                }
              : null,
        },
      };

      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: (data) => {
      // Update Redux store with the new marketplace settings
      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Shipping Updated', 'Your shipping settings have been saved successfully.');
    },
    onError: (error) => {
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update shipping settings', error);
    },
  });

  const onSubmit = (data: FormFields) => {
    updateShipping(data);
  };

  return (
    <SettingsMain>
      <SettingsPageHeader title="Flat Rate Shipping">
        <CommerceTypeAvatar commerceType={commerceType || CommerceType.None} />
      </SettingsPageHeader>
      <p className="text-muted small" style={{ marginTop: '4px' }}>
        Configure your shipping rates for domestic and international orders.
      </p>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ gap: '16px' }}>
        <Form.FieldGroup>
          <Form.Field
            labelText="Shipping From"
            id="shippingCountry"
            isRequired
            errorText={formState.errors.shippingCountry?.message}
          >
            <Controller
              name="shippingCountry"
              control={control}
              rules={{ required: 'Please select a shipping country' }}
              render={({ field }) => (
                <Select
                  options={
                    COUNTRIES.map((country) => ({
                      label: country.name,
                      value: country.code,
                    })) as ReactSelectOption[]
                  }
                  value={
                    COUNTRIES.find((country) => country.code === field.value)
                      ? {
                          label: COUNTRIES.find((country) => country.code === field.value)!.name,
                          value: field.value,
                        }
                      : null
                  }
                  onChange={(option) => field.onChange(option?.value)}
                  placeholder="Select shipping country"
                  isFullWidth
                />
              )}
            />
          </Form.Field>
          <ShippingSection>
            <SectionTitle variant="Headings/H4">Domestic</SectionTitle>
            <ShippingGroup column gap="16px">
              <Form.Field
                id="usFlatRate"
                labelText="Flat Rate"
                errorText={
                  formState.errors.shippingRates?.us?.flatRate?.message ||
                  formState.errors.shippingRates?.us?.flatRateMethod?.message
                }
              >
                <RateContainer>
                  <Input
                    type="number"
                    step="0.01"
                    {...register('shippingRates.us.flatRate', { valueAsNumber: true })}
                    placeholder="$0.00"
                    style={{ minWidth: '100px', maxWidth: '120px' }}
                  />
                  <Input
                    type="text"
                    {...register('shippingRates.us.flatRateMethod', {
                      validate: validateShippingMethod(
                        usFlatRate,
                        'Shipping method is required when flat rate is set'
                      ),
                    })}
                    placeholder="Enter shipping rate type"
                    style={{ flex: 1 }}
                  />
                </RateContainer>
              </Form.Field>

              <Form.Field
                id="usFreeShipping"
                labelText="Free Shipping"
                errorText={
                  formState.errors.shippingRates?.us?.freeShippingThreshold?.message ||
                  formState.errors.shippingRates?.us?.freeShippingMethod?.message
                }
              >
                <RateContainer>
                  <Input
                    type="number"
                    step="0.01"
                    {...register('shippingRates.us.freeShippingThreshold', {
                      valueAsNumber: true,
                    })}
                    placeholder="$0.00"
                    style={{ minWidth: '100px', maxWidth: '120px' }}
                  />
                  <Input
                    type="text"
                    {...register('shippingRates.us.freeShippingMethod', {
                      validate: validateShippingMethod(
                        usFreeShippingThreshold,
                        'Free shipping method is required when threshold is set'
                      ),
                    })}
                    placeholder="Enter shipping rate type"
                    style={{ flex: 1 }}
                  />
                </RateContainer>
              </Form.Field>
            </ShippingGroup>

            <SectionTitle variant="Headings/H4">International</SectionTitle>

            <Checkbox {...register('isInternationalShippingDisabled')}>
              We don&apos;t offer International Shipping
            </Checkbox>

            {!isInternationalDisabled && (
              <ShippingGroup column gap="16px">
                <Form.Field
                  id="internationalFlatRate"
                  labelText="Flat Rate"
                  errorText={
                    formState.errors.shippingRates?.international?.flatRate?.message ||
                    formState.errors.shippingRates?.international?.flatRateMethod?.message
                  }
                >
                  <RateContainer>
                    <Input
                      type="number"
                      step="0.01"
                      {...register('shippingRates.international.flatRate', {
                        valueAsNumber: true,
                      })}
                      placeholder="$0.00"
                      style={{ minWidth: '100px', maxWidth: '120px' }}
                    />
                    <Input
                      type="text"
                      {...register('shippingRates.international.flatRateMethod', {
                        validate: validateShippingMethod(
                          internationalFlatRate,
                          'International shipping method is required when flat rate is set'
                        ),
                      })}
                      placeholder="Enter shipping rate type"
                      style={{ flex: 1 }}
                    />
                  </RateContainer>
                </Form.Field>

                <Form.Field
                  id="internationalFreeShipping"
                  labelText="Free Shipping"
                  errorText={
                    formState.errors.shippingRates?.international?.freeShippingThreshold?.message ||
                    formState.errors.shippingRates?.international?.freeShippingMethod?.message
                  }
                >
                  <RateContainer>
                    <Input
                      type="number"
                      step="0.01"
                      {...register('shippingRates.international.freeShippingThreshold', {
                        valueAsNumber: true,
                      })}
                      placeholder="$0.00"
                      style={{ minWidth: '100px', maxWidth: '120px' }}
                    />
                    <Input
                      type="text"
                      {...register('shippingRates.international.freeShippingMethod', {
                        validate: validateShippingMethod(
                          internationalFreeShippingThreshold,
                          'International free shipping method is required when threshold is set'
                        ),
                      })}
                      placeholder="Enter shipping rate type"
                      style={{ flex: 1 }}
                    />
                  </RateContainer>
                </Form.Field>
              </ShippingGroup>
            )}
          </ShippingSection>
        </Form.FieldGroup>
        <Form.Footer justifyEnd>
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </Form.Footer>
      </Form>
    </SettingsMain>
  );
};

export default Shipping;
